/**
 * JkaboutComponent
 *
 * 多功能對話框組件，用於顯示應用程式相關資訊、文本內容以及 JSON 資料。
 * 可作為 "關於" 對話框、文本顯示視窗或 JSON 資料檢視器使用。
 *
 * @see jkabout.component.md 完整文檔
 */
import { Component, HostListener, inject, InjectionToken, Injector, Input, OnInit, input, ChangeDetectionStrategy } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
// import { JktextareaComponent } from '../jktextarea/jktextarea.component'; 會造成循環
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
  MatDialogContent,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import * as XLSX from 'xlsx';
import { FormsModule } from '@angular/forms';
import { jkLib, } from '../../services/jkCommon';
import { MaterialModule } from '../../material.module';
import { CR, idNameItem } from '../../services/jkInterface';
import { JkExcelService } from '../../services/jkexcel.service';

//export const MAT_MDC_DIALOG_DATA = new InjectionToken<any>('MatMdcDialogData');
const MAT_MDC_DIALOG_DATA = new InjectionToken<any>('MatMdcDialogData'); // #dialog

@Component({
  selector: 'app-jkabout',
  templateUrl: './jkabout.component.html',
  styleUrls: ['./jkabout.component.scss'],
  standalone: true,
  /*providers: [
    {
      provide: MatDialogRef,
      useValue: {
        close: () => {},
        afterClosed: () => ({ subscribe: () => {} })
      }
    },
    { provide: MAT_DIALOG_DATA, useValue: {} }
    ],*/
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    MaterialModule,
    ClipboardModule,
    //JktextareaComponent,
  ],
})
//export class JkaboutComponent extends JkBaseComponent implements OnInit {
/**
 * 關於對話框組件
 * 可用於顯示應用程式資訊、文本內容或 JSON 資料的對話框組件
 */
export class JkaboutComponent implements OnInit {
  dialogRef = inject<MatDialogRef<JkaboutComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);

  /** 組件類型識別 */
  comType: string = 'jkabout';
  /** 匯出檔案類型 */
  bookType: string = 'xlsx';
  /** 文本區域行數 */
  rows: number = 10;
  /** 最小高度 */
  minHeight: number = 30;
  /** 最大高度 */
  maxHeight: number = 300;
  /** 是否禁用 */
  disabled: boolean = false;
  /** 高度設定 */
  height: string = 'auto'; //'70px'; // 100%
  /** 當前選擇的檔案類型項目 */
  bookTypeItem: idNameItem;
  /** 是否可見 */
  visible: boolean = true;
  /** 對話框標籤文本 */
  label: string = '';

  /**
   * 取得可用的檔案類型列表
   * @returns 檔案類型列表，如果未初始化 Excel 服務則返回空陣列
   */
  get bookTypeList() {
    if (!this.xls)
      return [];
    return this.xls.bookTypeList;
  }

  /**
   * 執行匯出操作
   * 將 JSON 資料匯出為指定類型的檔案
   */
  doExport() {
    this.bookType = this.bookTypeItem.id;
    this.xls.jsonExport(this.jsonObj, jkLib.nowDate() + '-' + jkLib.nowTime6() + '.' + this.bookType, this.bookType as XLSX.BookType);
  }
  /** 組件內容值 */
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() value: string = '';
  /** 是否為模態對話框 */
  readonly isModal = input<boolean>(false);
  /** 平台資訊文本 */
  platformValue: string = '';
  /** 是否顯示關於資訊 */
  isAbout: boolean = false;
  /** 平台服務 */
  platform: Platform;
  /** 斷點觀察者服務 */
  private breakpointObserver: BreakpointObserver;
  /** 是否顯示 JSON 資料 */
  isJson: boolean = false;
  /** JSON 物件資料 */
  jsonObj: any;
  /** Excel 服務 */
  public xls: JkExcelService = inject(JkExcelService);
  /** 剪貼簿服務 */
  public clipboard: Clipboard = inject(Clipboard);
  //public data: any = inject(MAT_DIALOG_DATA);// #dialog

  /**
   * 建構函數
   * @param injector 注入器
   * @param dialogRef 對話框參考
   * @param data 對話框資料
   */
  constructor() {
    const injector = inject(Injector);

    //super();
    this.bookTypeItem = this.xls.bookTypeList[0];
    this.breakpointObserver = injector.get(BreakpointObserver);
    this.platform = injector.get(Platform);
    this.label = this.data.title || '';
    if ((this.data.content === '') && this.data.title === '關於') {
      this.isAbout = true;
      this.value = this.data.content;
      this.setPlatformValue();
      this.setUrl();
      this.setLoginInfo();
    } else {
      this.value = this.data.content;
    }

    if (this.label.toUpperCase() === 'JSON') {
      this.isJson = true;
      this.value = JSON.stringify(this.data.jsonObj);
      this.jsonObj = this.data.jsonObj;
    }
  }
  /**
   * 設定 URL 相關資訊
   * 獲取並設定當前應用程式的 URL 相關資訊，包括完整網址、協議、主機名和路徑
   */
  setUrl() {
    // 取得完整網址
    const currentUrl = window.location.href;
    this.platformValue += CR + ' 前端網址:' + currentUrl + CR;
    // 取得協議 (例如：http 或 https)
    const protocol = window.location.protocol;
    this.platformValue += ' 協議:' + protocol + CR;
    // 取得主機名 (例如：www.example.com)
    this.platformValue += ' 主機名:' + window.location.hostname + CR;
    // 取得路徑 (例如：/path/to/page)
    this.platformValue += ' 路徑:' + window.location.pathname + CR;
  }

  /**
   * 設定登入資訊
   * 註: 目前實作被註解掉
   */
  setLoginInfo() {/*
    this.platformValue += 'Login id:'+this.login.companyItem.id+CR;
    this.platformValue += 'userId:'+this.login.UserID+CR;
    this.platformValue += '公司名:'+this.login.companyItem.name+CR;
    this.platformValue += 'dbname:'+this.login.dbname+CR;
    this.platformValue += 'Customid:'+this.login.customid +CR+
    ' AP 網址:'+this.login.hostName+CR;*/
  }

  /**
   * 設定平台資訊值
   * 獲取並設定有關用戶平台的詳細資訊，包括瀏覽器類型、裝置尺寸等
   */
  setPlatformValue() {
    this.platformValue =
      'platform:' + jkLib.iif(this.platform.ANDROID, ' android ', '')
      + jkLib.iif(this.platform.isBrowser, ' Browser ', '')
      + jkLib.iif(this.platform.BLINK, ' BLINK ', '')
      + jkLib.iif(this.platform.EDGE, ' EDGE ', '')
      + jkLib.iif(this.platform.FIREFOX, ' FIREFOX ', '')
      + jkLib.iif(this.platform.IOS, ' IOS ', '')
      + jkLib.iif(this.platform.SAFARI, ' SAFARI ', '')
      + jkLib.iif(this.platform.TRIDENT, ' TRIDENT ', '')
      + jkLib.iif(this.platform.WEBKIT, ' WEBKIT ', '')
      + CR; //.platforms().join(',')+CR;
    this.platformValue += ' window.innerWidth:' + window.innerWidth + CR
      + ' window.innerHeight:' + window.innerHeight + CR;
    /*this.platformValue += 'isLandscape:'+this.platform.isLandscape()+CR;
    this.platformValue += 'isPortrait:'+this.platform.isPortrait()+CR;
    this.platformValue += 'height:'+this.platform.height()+CR;
    this.platformValue += 'width:'+this.platform.width()+CR;
    this.platformValue += 'url:'+this.platform.url()+CR; */
    //this.platformValue += 'product-version:'+this.platform.testUserAgent('product-version')+CR;
  }

  /**
   * 組件初始化
   */
  ngOnInit() {
  }

  /**
   * 監聽瀏覽器後退按鈕事件
   * 註: 目前實作被註解掉
   */
  @HostListener('window:popstate')
  onPopState() {
    /*if (isPlatform('android') && isPlatform('mobileweb')) {
      this.modalCtrl.dismiss();
    }*/
    //this.dialog.closeAll();
  }

  /**
   * 關閉對話框
   * 若為模態對話框，則關閉對話框
   */
  onDismiss() {
    if (this.isModal()) {
      //this.modalCtrl.dismiss();
      //this.dialog.closeAll();
    }
  }

  /**
   * 檢查裝置方向
   * 使用 BreakpointObserver 檢測裝置是否處於橫向模式
   * @returns 描述裝置方向的字串
   */
  checkOrientation(): string {
    /*if (this.platform.isLandscape()) {
    console.log('The device is in landscape mode.');
  } else {
    console.log('The device is not in landscape mode.');
  }*/
    let ret = '';
    if (this.breakpointObserver.isMatched(Breakpoints.HandsetLandscape)) {
      //console.log('The device is in landscape mode.');
      ret = 'The device is in landscape mode.';
    } else {
      //console.log('The device is not in landscape mode.');
      ret = 'The device is not in landscape mode.';
    }
    return ret;
  }

  /**
   * 處理對話框關閉事件
   * @param result 對話框結果
   */
  onNoClick(result: boolean): void {
    this.data.content = this.value;
    this.data.result = result;
    if (this.dialogRef && typeof this.dialogRef.close === 'function') {
      this.dialogRef.close(this.data);
    }
  }

  /**
   * 複製對話框內容到剪貼簿
   * 使用 Clipboard 服務將當前內容複製到系統剪貼簿
   */
  async doCopy() {
    this.clipboard.copy(this.value);
    return;
    let textToCopy = '';
    if (this.isJson) {
      //textToCopy = JSON.stringify(this.jsonObj);
      this.clipboard.copy(this.value);
    } else {
      //textToCopy = this.data.content; // 要複製的文本
      await this.clipboard.beginCopy(this.value);
    }
    //let r = await this.clipboard.read();
    //console.log('clipboard.read()',r);
    /*let textarea = document.createElement('textarea');
    textarea.value = textToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);*/
  }
}
